import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  ViewChild,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  BPMN_SEARCH_CONFIG,
  BPMN_COLUMNS,
  BPMN_STATUS,
  BPMN_DEFAULT_COLUMNS_ORDER,
  BPMN_FLEXIBLE_COLUMNS_ORDER,
  BPMN_ACTION_COLUMN,
  BpmnItem,
} from './bpmn.config';
import { BpmnService } from './services/bpmn.service';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import { HttpErrorResponse } from '@angular/common/http';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';

@Component({
  selector: 'app-bpmn-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './bpmn-list.component.html',
  styleUrls: ['./bpmn-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpmnListComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly bpmnService = inject(BpmnService);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly cdr = inject(ChangeDetectorRef);

  // Search configuration
  searchConfig = BPMN_SEARCH_CONFIG;

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      BPMN_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of BPMN_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if user has any action permissions
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(BPMN_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      BPMN_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of BPMN_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Permission flags - using menu codes: settings.master_data.workflow_design.view/create/edit/delete/approve
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.master_data.workflow_design.view',
    );
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.master_data.workflow_design.create',
    );
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.master_data.workflow_design.edit',
    );
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.master_data.workflow_design.delete',
    );
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.master_data.workflow_design.approve',
    );
  });

  // Check if user has any action permissions
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canUpdate() ||
      this.canApprove() ||
      this.canDelete(),
  );

  // Button configuration
  primaryButton = computed(
    (): ActionButton => ({
      label: 'bpmn.actions.create_process',
      severity: 'info',
      show: this.canCreate(),
      icon: 'add',
    }),
  );

  // Data signals
  bpmnProcesses = signal<BpmnItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    processName: '',
    page: 1,
    size: 20,
  });

  // Selection
  selectedProcesses = signal<BpmnItem[]>([]);

  // Computed properties
  hasSelectedProcesses = computed(() => this.selectedProcesses().length > 0);

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedProcess: BpmnItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'deploy' | null = null;

  private readonly destroy$ = new Subject<void>();

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  // Date formatting using formatDateTime utility
  formatDateTime(dateInput: any): string {
    return formatDateTime(dateInput);
  }

  ngOnInit(): void {
    this.loadBpmnProcesses();
    // Subscribe to permissions loaded to trigger change detection
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  onSearch(params: any): void {
    this.searchParams.set({ ...this.searchParams(), ...params, page: 1 });
    this.loadBpmnProcesses();
  }

  onReset(): void {
    // Check if there's any data to reset
    const currentParams = this.searchParams();
    const hasDataToReset = currentParams.processName;

    if (!hasDataToReset) {
      return;
    }

    // Reset search parameters to default values
    this.searchParams.set({
      processName: '',
      page: 1,
      size: 20,
    });

    // Clear any selections
    this.selectedProcesses.set([]);

    // Reload processes with default parameters
    this.loadBpmnProcesses();
  }

  onPageChange(event: any): void {
    // Convert 0-based page from advanced-data-table to 1-based for service
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1,
      size: event.rows,
    });
    this.loadBpmnProcesses();
  }

  onSelectionChange(selection: BpmnItem[]): void {
    this.selectedProcesses.set(selection);
  }

  onAdd(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/configuration/master-data/bpmn/add']);
  }

  onOpenEngineSettings(): void {
    this.router.navigate(['/configuration/master-data/workflow-engine']);
  }

  onView(process: BpmnItem): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/configuration/master-data/bpmn/view', process.id]);
  }

  onEdit(process: BpmnItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/configuration/master-data/bpmn/edit', process.id]);
  }

  onDeploy(process: BpmnItem): void {
    // Check permission
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    // Prevent action if button is disabled
    if (this.isDeployDisabled(process.status)) {
      return;
    }

    this.selectedProcess = process;
    this.dialogTitle = 'bpmn.dialog.confirm_deploy.title';
    this.dialogMessage = this.translateService.instant(
      'bpmn.dialog.confirm_deploy.message',
      { processName: process.name },
    );
    this.currentAction = 'deploy';
    this.dialogButtons = [
      {
        label: 'bpmn.dialog.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: 'bpmn.dialog.actions.deploy',
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onDelete(process: BpmnItem): void {
    // Check permission
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    // Prevent action if button is disabled
    if (this.isDeleteDisabled(process.status)) {
      return;
    }

    this.selectedProcess = process;
    this.dialogTitle = 'bpmn.dialog.confirm_delete.title';
    this.dialogMessage = this.translateService.instant(
      'bpmn.dialog.confirm_delete.message',
      { processName: process.name },
    );
    this.currentAction = 'delete';
    this.dialogButtons = [
      {
        label: 'bpmn.dialog.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: 'bpmn.dialog.actions.delete',
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  private downloadBpmnFile(process: BpmnItem): void {
    this.loading.set(true);
    this.bpmnService.downloadBpmnFile(process).subscribe({
      next: (success) => {
        this.loading.set(false);
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Download Successful',
            detail: `File "${process.fileName}" has been downloaded successfully.`,
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Download Failed',
            detail: 'Failed to download the file. Please try again.',
          });
        }
      },
      error: (error) => {
        console.error('Download error:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Download Error',
          detail: 'An error occurred while downloading the file.',
        });
      },
    });
  }

  private loadBpmnProcesses(): void {
    this.loading.set(true);
    this.bpmnService.getBpmnProcesses(this.searchParams()).subscribe({
      next: (response) => {
        this.bpmnProcesses.set(response.data);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading BPMN processes:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('bpmn.messages.error.title'),
          detail: this.translateService.instant(
            'bpmn.messages.error.failed_to_load_processes',
          ),
        });
      },
    });
  }

  private deployProcess(id: string): void {
    this.loading.set(true);
    this.bpmnService.deployProcess(id).subscribe({
      next: (success) => {
        this.loading.set(false);
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'bpmn.messages.success.title',
            ),
            detail: this.translateService.instant(
              'bpmn.messages.success.process_deployed',
            ),
          });
          // Refresh the list to show updated status
          this.loadBpmnProcesses();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('bpmn.messages.error.title'),
          detail: error.error.message,
          life: 5000,
        });
        this.loading.set(false);
      },
    });
  }

  private deleteProcess(id: string): void {
    this.bpmnService.deleteProcess(id).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'bpmn.messages.success.title',
            ),
            detail: this.translateService.instant(
              'bpmn.messages.success.process_deleted',
            ),
          });
          this.loadBpmnProcesses();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('bpmn.messages.error.title'),
            detail: this.translateService.instant(
              'bpmn.messages.error.failed_to_delete_process',
            ),
          });
        }
      },
      error: (error) => {
        console.error('Error deleting process:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('bpmn.messages.error.title'),
          detail: this.translateService.instant(
            'bpmn.messages.error.failed_to_delete_process',
          ),
        });
      },
    });
  }

  // Status methods
  getStatusSeverity(status: number): 'success' | 'danger' {
    return status === BPMN_STATUS.DEPLOYED ? 'success' : 'danger';
  }

  getStatusDisplayText(status: number): string {
    return this.translateService.instant(
      status === BPMN_STATUS.DEPLOYED
        ? 'bpmn.status.deployed'
        : 'bpmn.status.not_deployed',
    );
  }

  getStatusIcon(status: number): string {
    return status === BPMN_STATUS.DEPLOYED
      ? 'pi pi-check-circle text-green-500'
      : 'pi pi-times-circle text-red-500';
  }

  getStatusStyle(status: number): any {
    return status === BPMN_STATUS.DEPLOYED
      ? {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        }
      : {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
  }

  // Check if deploy/delete buttons should be disabled
  isDeployDisabled(status: number): boolean {
    return status === BPMN_STATUS.DEPLOYED; // Disable deploy when already deployed
  }

  isDeleteDisabled(status: number): boolean {
    return status === BPMN_STATUS.DEPLOYED; // Disable delete when deployed
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'delete') {
        this.confirmDelete();
      } else if (this.currentAction === 'deploy') {
        this.confirmDeploy();
      }
    }
  }

  confirmDelete(): void {
    if (this.selectedProcess) {
      this.deleteProcess(this.selectedProcess.id);
    }
    this.closeConfirmationDialog();
  }

  confirmDeploy(): void {
    if (this.selectedProcess) {
      this.deployProcess(this.selectedProcess.id);
    }
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedProcess = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
